import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "../lib/api";
import { useBranch } from "../context/BranchContext";
import { PageHeader } from "../components/shared";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import { Plus, Pencil, Search, CalendarPlus, AlertTriangle } from "lucide-react";
import { istNow, ymd } from "../lib/dates";

const SOURCES = ["Walk-in", "Phone", "WhatsApp", "Instagram", "Facebook", "Referral", "Website", "Other"];
const STATUSES = ["New", "Follow-up", "Appointment Booked", "Converted", "Lost"];
const STATUS_STYLE = {
  New: "bg-blue-900/40 text-blue-300 border-blue-800",
  "Follow-up": "bg-amber-900/40 text-amber-300 border-amber-800",
  "Appointment Booked": "bg-purple-900/40 text-purple-300 border-purple-800",
  Converted: "bg-green-900/40 text-green-300 border-green-800",
  Lost: "bg-red-900/40 text-red-300 border-red-800",
};
const today = () => ymd(istNow());
const emptyForm = (bid) => ({ customer_name: "", mobile: "", branch_id: bid || "", source: "Walk-in", interested_service: "", assigned_staff_id: "", assigned_staff_name: "", enquiry_date: today(), follow_up_date: "", status: "New", notes: "" });

export default function Enquiries() {
  const { branches, branchParam } = useBranch();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState({});
  const [statusFilter, setStatusFilter] = useState("all");
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [staff, setStaff] = useState([]);
  const [services, setServices] = useState([]);
  const [dup, setDup] = useState(null);

  const load = () => {
    const params = { branch_id: branchParam };
    if (statusFilter !== "all") params.status = statusFilter;
    api.get("/enquiries", { params }).then((r) => setRows(r.data));
    api.get("/enquiries/stats", { params: { branch_id: branchParam } }).then((r) => setStats(r.data));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [branchParam, statusFilter]);

  useEffect(() => {
    if (form.branch_id) {
      api.get("/staff", { params: { branch_id: form.branch_id } }).then((r) => setStaff(r.data)).catch(() => setStaff([]));
      api.get("/services", { params: { branch_id: form.branch_id } }).then((r) => setServices(r.data)).catch(() => setServices([]));
    }
  }, [form.branch_id]);

  const openNew = () => { setForm(emptyForm(branches[0]?.id)); setEditId(null); setDup(null); setOpen(true); };
  const openEdit = (r) => { setForm({ ...emptyForm(), ...r }); setEditId(r.id); setDup(null); setOpen(true); };

  const doSave = async (force = false) => {
    const payload = { ...form };
    if (payload.assigned_staff_id) payload.assigned_staff_name = staff.find((s) => s.id === payload.assigned_staff_id)?.name || "";
    try {
      if (editId) await api.put(`/enquiries/${editId}`, payload);
      else await api.post(`/enquiries${force ? "?force=true" : ""}`, payload);
      toast.success(editId ? "Enquiry updated" : "Enquiry added");
      setDup(null); setOpen(false); load();
    } catch (e) {
      if (e.response?.status === 409 && e.response.data?.detail?.existing) setDup(e.response.data.detail.existing);
      else toast.error(apiError(e.response?.data?.detail));
    }
  };

  const save = () => {
    if (!form.customer_name.trim() || !form.mobile.trim()) { toast.error("Name and mobile are required"); return; }
    if (!form.branch_id) { toast.error("Select a branch"); return; }
    setDup(null);
    doSave(false);
  };

  const quickStatus = async (r, status) => {
    try { await api.put(`/enquiries/${r.id}`, { ...r, status }); toast.success(`Marked ${status}`); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const bookAppointment = async (r) => {
    try { await api.post(`/enquiries/${r.id}/convert`); } catch { /* still navigate */ }
    const svc = services.find((s) => s.name === r.interested_service);
    navigate("/appointments", { state: { prefillService: {
      id: svc?.id || "", name: r.interested_service || "", duration: svc?.duration || 30,
      branch_id: r.branch_id, customer_name: r.customer_name, customer_mobile: r.mobile,
      staff_id: r.assigned_staff_id || "",
    } } });
  };

  const todayStr = today();
  const isOverdue = (r) => r.follow_up_date && r.follow_up_date < todayStr && !["Converted", "Lost"].includes(r.status);
  const isDueToday = (r) => r.follow_up_date === todayStr && !["Converted", "Lost"].includes(r.status);
  const overdueCount = rows.filter(isOverdue).length;
  const dueCount = rows.filter((r) => isOverdue(r) || isDueToday(r)).length;

  const term = q.trim().toLowerCase();
  const filtered = rows.filter((r) => !term || [r.customer_name, r.mobile, r.interested_service].some((f) => (f || "").toLowerCase().includes(term)));

  return (
    <div>
      <PageHeader title="Enquiries / Leads" subtitle="Track walk-in, phone & WhatsApp leads to conversion — branch-wise"
        actions={<Button onClick={openNew} data-testid="add-enquiry-btn" className="rounded-sm bg-primary text-primary-foreground hover:bg-[#D4AF37]"><Plus size={16} className="mr-1" /> Add Enquiry</Button>} />

      <div className="grid grid-cols-3 sm:grid-cols-6 gap-3 mb-5">
        {["total", ...STATUSES].map((k) => (
          <Card key={k} className="p-3 bg-card border-border" data-testid={`enq-stat-${k}`}>
            <div className="text-xs text-muted-foreground capitalize">{k === "total" ? "Total" : k}</div>
            <div className="text-2xl font-display text-primary">{stats[k] ?? 0}</div>
          </Card>
        ))}
      </div>

      {dueCount > 0 && (
        <div className={`mb-4 flex items-center gap-2 rounded-sm border px-4 py-2 text-sm ${overdueCount > 0 ? "border-red-800 bg-red-900/20 text-red-300" : "border-amber-800 bg-amber-900/20 text-amber-300"}`} data-testid="enq-followup-banner">
          <AlertTriangle size={16} /> {dueCount} follow-up{dueCount > 1 ? "s" : ""} due today or overdue{overdueCount > 0 ? ` (${overdueCount} overdue)` : ""} — assigned staff get a daily WhatsApp nudge.
        </div>
      )}

      <div className="flex flex-wrap gap-3 mb-4 items-center">
        <div className="relative max-w-xs flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name / mobile / service…" data-testid="enq-search" className="bg-secondary pl-9" />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="bg-secondary w-52" data-testid="enq-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <Card className="bg-card border-border overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="border-border hover:bg-transparent">
              <TableHead>Customer</TableHead><TableHead>Mobile</TableHead><TableHead>Branch</TableHead>
              <TableHead>Source</TableHead><TableHead>Service</TableHead><TableHead>Staff</TableHead>
              <TableHead>Enquiry</TableHead><TableHead>Follow-up</TableHead><TableHead>Status</TableHead><TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map((r) => (
              <TableRow key={r.id} className={`border-border ${isOverdue(r) ? "bg-red-900/15" : isDueToday(r) ? "bg-amber-900/15" : ""}`} data-testid={`enquiry-row-${r.id}`}>
                <TableCell className="font-medium">{r.customer_name}</TableCell>
                <TableCell>{r.mobile}</TableCell>
                <TableCell className="text-xs text-muted-foreground">{branches.find((b) => b.id === r.branch_id)?.name || "—"}</TableCell>
                <TableCell><Badge variant="outline">{r.source}</Badge></TableCell>
                <TableCell className="text-muted-foreground">{r.interested_service || "—"}</TableCell>
                <TableCell className="text-muted-foreground">{r.assigned_staff_name || "—"}</TableCell>
                <TableCell className="text-xs">{r.enquiry_date}</TableCell>
                <TableCell className={`text-xs ${isOverdue(r) ? "text-red-400 font-medium" : isDueToday(r) ? "text-amber-400 font-medium" : ""}`}>
                  {r.follow_up_date || "—"}{isOverdue(r) && " ⚠"}
                </TableCell>
                <TableCell>
                  <Select value={r.status} onValueChange={(v) => quickStatus(r, v)}>
                    <SelectTrigger className={`h-7 w-40 text-xs border ${STATUS_STYLE[r.status] || ""}`} data-testid={`enq-status-${r.id}`}><SelectValue /></SelectTrigger>
                    <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </TableCell>
                <TableCell>
                  <div className="flex gap-1">
                    <Button size="sm" variant="ghost" className="h-8 text-xs text-primary" onClick={() => bookAppointment(r)} data-testid={`enq-book-${r.id}`} title="Book Appointment & mark Converted"><CalendarPlus size={14} className="mr-1" /> Book</Button>
                    <Button size="sm" variant="ghost" className="h-8" onClick={() => openEdit(r)} data-testid={`enq-edit-${r.id}`}><Pencil size={14} /></Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {!filtered.length && <TableRow><TableCell colSpan={10} className="text-center text-muted-foreground py-10">No enquiries yet.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setDup(null); }}>
        <DialogContent className="bg-card border-border max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">{editId ? "Edit Enquiry" : "Add Enquiry"}</DialogTitle>
            <DialogDescription>Record a lead and track it to conversion. All fields are stored branch-wise.</DialogDescription>
          </DialogHeader>
          {dup && (
            <div className="rounded-sm border border-amber-800 bg-amber-900/20 p-3 text-sm" data-testid="enq-duplicate-warning">
              <div className="flex items-center gap-2 text-amber-300 font-medium mb-1"><AlertTriangle size={15} /> Possible duplicate lead</div>
              <p className="text-muted-foreground">An enquiry for <span className="text-foreground">{dup.mobile}</span> already exists in this branch:</p>
              <div className="mt-2 text-xs text-muted-foreground">
                <div><span className="text-foreground">{dup.customer_name}</span> · {dup.source} · {dup.interested_service || "no service"}</div>
                <div>Enquiry {dup.enquiry_date} · Status <span className="text-foreground">{dup.status}</span>{dup.follow_up_date ? ` · Follow-up ${dup.follow_up_date}` : ""}</div>
              </div>
              <div className="flex gap-2 mt-3">
                <Button size="sm" variant="outline" className="rounded-sm" data-testid="enq-open-existing" onClick={() => { setOpen(false); setTimeout(() => openEdit(dup), 60); }}>Open Existing</Button>
                <Button size="sm" variant="outline" className="rounded-sm border-amber-700 text-amber-300" data-testid="enq-add-anyway" onClick={() => doSave(true)}>Add Anyway</Button>
              </div>
            </div>
          )}
          <div className="space-y-3 max-h-[70vh] overflow-y-auto pr-1">
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Customer Name</Label><Input value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} className="bg-secondary mt-1" data-testid="enq-name" /></div>
              <div><Label className="text-xs">Mobile Number</Label><Input value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} className="bg-secondary mt-1" data-testid="enq-mobile" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Branch</Label>
                <Select value={form.branch_id} onValueChange={(v) => setForm({ ...form, branch_id: v, assigned_staff_id: "" })}>
                  <SelectTrigger className="bg-secondary mt-1" data-testid="enq-branch"><SelectValue placeholder="Select branch" /></SelectTrigger>
                  <SelectContent>{branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label className="text-xs">Enquiry Source</Label>
                <Select value={form.source} onValueChange={(v) => setForm({ ...form, source: v })}>
                  <SelectTrigger className="bg-secondary mt-1" data-testid="enq-source"><SelectValue /></SelectTrigger>
                  <SelectContent>{SOURCES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Interested Service</Label>
                <Select value={form.interested_service || "none"} onValueChange={(v) => setForm({ ...form, interested_service: v === "none" ? "" : v })}>
                  <SelectTrigger className="bg-secondary mt-1" data-testid="enq-service"><SelectValue placeholder="Select service" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">—</SelectItem>
                    {services.map((s) => <SelectItem key={s.id} value={s.name}>{s.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div><Label className="text-xs">Assigned Staff</Label>
                <Select value={form.assigned_staff_id || "none"} onValueChange={(v) => setForm({ ...form, assigned_staff_id: v === "none" ? "" : v })}>
                  <SelectTrigger className="bg-secondary mt-1" data-testid="enq-staff"><SelectValue placeholder="Select staff" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">—</SelectItem>
                    {staff.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Enquiry Date</Label><Input type="date" value={form.enquiry_date} onChange={(e) => setForm({ ...form, enquiry_date: e.target.value })} className="bg-secondary mt-1" data-testid="enq-date" /></div>
              <div><Label className="text-xs">Follow-up Date</Label><Input type="date" value={form.follow_up_date} onChange={(e) => setForm({ ...form, follow_up_date: e.target.value })} className="bg-secondary mt-1" data-testid="enq-followup" /></div>
            </div>
            <div><Label className="text-xs">Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger className="bg-secondary mt-1" data-testid="enq-form-status"><SelectValue /></SelectTrigger>
                <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs">Notes</Label><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="bg-secondary mt-1" data-testid="enq-notes" /></div>
          </div>
          <DialogFooter><Button onClick={save} data-testid="enq-save" className="rounded-sm bg-primary text-primary-foreground hover:bg-[#D4AF37]">{editId ? "Save Changes" : "Add Enquiry"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
